import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../ButtonLink';
import ArrowLeft from '~assets/svg/arrow-left.svg';
import ArrowRight from '~assets/svg/arrow-right.svg';

import * as styles from './Slider.module.css';

type SliderProps = {
  children: React.ReactElement[];
  slideWidth?: number;
  gapWidth?: number;
  className?: string;
};

export const Slider: React.FC<SliderProps> = ({
  children,
  slideWidth = 308,
  gapWidth = 16,
  className,
}): JSX.Element => {
  const [scrollLeft, setScrollLeft] = useState<number>(0);
  const [showStepper, setShowStepper] = useState<boolean>(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const offset = slideWidth + gapWidth;
  const allSlidesWidth = (slideWidth + gapWidth) * children.length - gapWidth;

  const forward: React.MouseEventHandler = (): void => {
    if (!containerRef.current) return;
    const remainder = containerRef.current.scrollLeft % (slideWidth + gapWidth);
    containerRef.current.scrollBy({
      left: remainder > offset / 2 ? offset * 2 - remainder : offset - remainder,
      behavior: 'smooth',
    });
  };

  const backward: React.MouseEventHandler = (): void => {
    if (!containerRef.current) return;
    const remainder = containerRef.current.scrollLeft % (slideWidth + gapWidth);
    containerRef.current.scrollBy({
      left: remainder > offset / 2 ? -remainder : (offset + remainder) * -1,
      behavior: 'smooth',
    });
  };

  const isForwardDisabled = (): boolean => {
    if (!containerRef.current) return false;
    return scrollLeft + containerRef.current.offsetWidth === containerRef.current.scrollWidth;
  };

  const isBackwardDisabled = (): boolean => scrollLeft === 0;

  const scrollHandler: React.UIEventHandler<HTMLDivElement> = (event): void => {
    const target = event.target as HTMLDivElement;
    setScrollLeft(target.scrollLeft);
  };

  const resizeHandler = useCallback(() => {
    setShowStepper(contentRef.current?.offsetWidth === allSlidesWidth);
  }, []);

  useEffect(() => {
    resizeHandler();
    window.addEventListener('resize', resizeHandler);
    () => window.removeEventListener('resize', resizeHandler);
  }, []);

  return (
    <div className={className}>
      <div className={styles.container} ref={containerRef} onScroll={scrollHandler}>
        <div className={styles.content} ref={contentRef} style={{ columnGap: `${gapWidth}px` }}>
          {children}
        </div>
      </div>
      <div className={clsx(styles.stepper, showStepper && styles.showStepper)}>
        <Button
          className={styles.button}
          outline
          hideHoverStateOnFocus
          disabled={isBackwardDisabled()}
          onClick={backward}
        >
          <ArrowLeft className={styles.icon} />
        </Button>
        <Button
          className={styles.button}
          outline
          hideHoverStateOnFocus
          disabled={isForwardDisabled()}
          onClick={forward}
        >
          <ArrowRight className={styles.icon} />
        </Button>
      </div>
    </div>
  );
};
